import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { channelWa, flows, ussd } from '@repletoryreach/sdk-api';
import { Card, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useBots, botName } from '../../../../lib/bots';
import { useT } from '../../../../i18n/I18nProvider';

interface Account {
  channel: flows.BotChannel;
  id: string;
  label: string;
}

/**
 * Where a chatbot answers (21 September 2026): every WhatsApp number, website
 * chat, Facebook Page and Instagram account the organisation has, ticked where
 * this chatbot is the one that answers.
 *
 * One chatbot answers on each account. An account nobody has chosen a chatbot
 * for is the default chatbot's, which is why the default chatbot's own ticks
 * cannot be cleared here: an account has to go TO a chatbot, so it is moved by
 * ticking it on the other one.
 */
export function BotChannelsCard({ bot }: { bot: flows.Bot }) {
  const t = useT();
  const qc = useQueryClient();
  const { bots, defaultBot } = useBots();
  const fallback = t('settings.assistant.defaultName');

  const numbers = useQuery({ queryKey: ['numbers'], queryFn: () => channelWa.listPhoneNumbers(), staleTime: 60_000 });
  const sites = useQuery({ queryKey: ['web-sites'], queryFn: () => flows.listWebSites(), staleTime: 60_000 });
  const meta = useQuery({ queryKey: ['meta-accounts'], queryFn: () => flows.listMetaAccounts(), staleTime: 60_000 });
  const links = useQuery({ queryKey: ['bots', 'channel-links'], queryFn: () => flows.listBotChannelLinks() });
  // USSD codes (4 October 2026): a code answers with the flows of the bot it is given to. Asked
  // without retries, so an account with no USSD (or a service that is not there) simply lists none.
  const ussdCodes = useQuery({ queryKey: ['ussd-codes'], queryFn: () => ussd.listCodes(), staleTime: 60_000, retry: false });

  const accounts = useMemo<Account[]>(() => [
    ...(numbers.data?.data ?? []).filter((n) => n.is_active).map((n) => ({
      channel: 'whatsapp' as const, id: n.id, label: [n.display_name, n.msisdn].filter(Boolean).join(' · ') || n.meta_phone_number_id
    })),
    ...(sites.data?.data ?? []).filter((s) => s.is_active).map((s) => ({ channel: 'web' as const, id: s.id, label: s.name })),
    ...(meta.data?.data ?? []).map((a) => ({ channel: a.channel as flows.BotChannel, id: a.id, label: a.label })),
    ...(ussdCodes.data?.data ?? []).filter((c) => c.status !== 'released').map((c) => ({ channel: 'ussd' as const, id: c.id, label: c.code }))
  ], [numbers.data, sites.data, meta.data, ussdCodes.data]);

  /** The chatbot that answers on an account: the one it was given to, else the default. */
  const answeredBy = (a: Account) => {
    const link = (links.data?.data ?? []).find((l) => l.channel === a.channel && l.account_id === a.id);
    return link?.bot_id ?? defaultBot?.id ?? null;
  };

  const move = useMutation({
    mutationFn: async ({ account, on }: { account: Account; on: boolean }) => {
      const ref = { channel: account.channel, account_id: account.id };
      // To the default chatbot is "to nobody in particular": the link goes,
      // and the account follows whichever chatbot is the default.
      if (!on || bot.is_default) await flows.unlinkChannel(ref);
      else await flows.linkChannelToBot({ ...ref, bot_id: bot.id });
      return { account, on };
    },
    onSuccess: ({ account, on }) => {
      void qc.invalidateQueries({ queryKey: ['bots'] });
      notifySuccess(
        on ? t('bots.channels.moved', { account: account.label, name: botName(bot, fallback) })
           : t('bots.channels.returned', { account: account.label, name: botName(defaultBot, fallback) }),
        t('bots.channels.title')
      );
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('bots.channels.title'))
  });

  function toggle(account: Account, on: boolean) {
    const current = bots.find((b) => b.id === answeredBy(account));
    // Taking an account from a chatbot somebody chose for it is worth a pause;
    // taking it from the default, which only had it by default, is not.
    if (on && current && current.id !== bot.id && !current.is_default) {
      openConfirmModal({
        title: t('bots.channels.confirm.title'),
        children: <Text role="bodySmall">{t('bots.channels.confirm.body', { account: account.label, from: botName(current, fallback), to: botName(bot, fallback) })}</Text>,
        labels: { confirm: t('bots.channels.confirm.action'), cancel: t('action.cancel') },
        onConfirm: () => move.mutate({ account, on })
      });
      return;
    }
    move.mutate({ account, on });
  }

  const loading = numbers.isPending || sites.isPending || meta.isPending || links.isPending;
  const mine = accounts.filter((a) => answeredBy(a) === bot.id);

  return (
    <Card withBorder>
      <Stack gap="md">
        <div>
          <Title order={5}>{t('bots.channels.title')}</Title>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">
            {bot.is_default ? t('bots.channels.introDefault') : t('bots.channels.intro')}
          </Text>
        </div>
        {bot.flow_count === 0 && mine.length > 0 && (
          <Alert color="warning">
            {t('bots.channels.noFlows', { name: botName(bot, fallback) })} <Link to="/flows">{t('nav.flows')}</Link>
          </Alert>
        )}
        {loading ? (
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>
        ) : accounts.length === 0 ? (
          <Text role="bodySmall" c="var(--rr-color-text-muted)">
            {t('bots.channels.none')} <Link to="/numbers">{t('nav.numbers')}</Link>
          </Text>
        ) : (
          <Stack gap="sm">
            {accounts.map((a) => {
              const owner = bots.find((b) => b.id === answeredBy(a));
              const checked = owner?.id === bot.id;
              return (
                <Checkbox
                  key={`${a.channel}:${a.id}`}
                  label={`${t(`bots.channels.kind.${a.channel}`)}: ${a.label}`}
                  description={checked
                    ? (bot.is_default ? t('bots.channels.mineByDefault') : undefined)
                    : t('bots.channels.answeredBy', { name: botName(owner, fallback) })}
                  checked={checked}
                  disabled={move.isPending || (checked && bot.is_default)}
                  onChange={(on) => toggle(a, on)}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
